import { createDriver as createAxiosDriver } from '@redux-requests/axios';
import {
  abortRequests,
  Driver,
  getQuery,
  handleRequests,
} from '@redux-requests/core';
import { createDriver } from '@redux-requests/promise';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import axios from 'axios';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { i18nSlice } from 'modules/i18n/i18nSlice';
import { LAYOUT_STATE_NAME, layoutReducer } from 'modules/layout/store/layout';
import { persistReducer, persistStore } from 'redux-persist';
import createSagaMiddleware from 'redux-saga';
import { setAccount } from '../modules/account/store/actions/setAccount';
import { extractMessage } from '../modules/common/utils/extractError';
import { historyInstance } from '../modules/common/utils/historyInstance';
import { NotificationActions } from '../modules/notification/store/NotificationActions';
import { notificationSlice } from '../modules/notification/store/notificationSlice';
import { rootSaga } from './rootSaga';
import { i18nPersistConfig } from './webStorageConfigs';
import {
  BlockchainNetworkId,
  DefaultTokenSymbol,
  ZERO_ADDRESS,
} from '../modules/common/conts';
import { Address } from '../modules/common/types/unit';
import { TokenSymbol } from '../modules/common/types/TokenSymbol';
import { disconnect } from 'modules/account/store/actions/disconnect';
import { likeSlice } from 'modules/common/store/like';
import { getChainId } from 'modules/common/utils/localStorage';
import { userSlice } from 'modules/common/store/user';
import { getNotWeb3WalletInfo } from 'modules/account/hooks/useWeb3React';

type MainApiDriverName =
  | 'mainApiEthMainnet'
  | 'mainApiEthRinkeby'
  | 'mainApiSmartchain'
  | 'mainApiHeco'
  | 'mainApiMatic'
  | 'mainApiAirdrop';

const chainToMainApiDriver: {
  [key in BlockchainNetworkId]: MainApiDriverName | undefined;
} = {
  [BlockchainNetworkId.mainnet]: 'mainApiEthMainnet',
  [BlockchainNetworkId.ropsten]: undefined,
  [BlockchainNetworkId.rinkeby]: 'mainApiEthRinkeby',
  [BlockchainNetworkId.goerli]: undefined,
  [BlockchainNetworkId.dev]: undefined,
  [BlockchainNetworkId.classic]: undefined,
  [BlockchainNetworkId.mordor]: undefined,
  [BlockchainNetworkId.kotti]: undefined,
  [BlockchainNetworkId.smartchain]: 'mainApiSmartchain',
  [BlockchainNetworkId.smartchainTestnet]: undefined,
  [BlockchainNetworkId.heco]: 'mainApiHeco',
  [BlockchainNetworkId.matic]: 'mainApiMatic',
};

function getMainApiDriverName(chainId: BlockchainNetworkId): MainApiDriverName {
  return chainToMainApiDriver[chainId] || 'mainApiEthMainnet';
}

function getAirdropMainApiDriverName(): MainApiDriverName {
  return 'mainApiAirdrop';
}

type NftViewApiDriverName =
  | 'nftViewApiEthMainnet'
  | 'nftViewApiEthRinkeby'
  | 'nftViewApiSmartchain'
  | 'nftViewApiHeco'
  | 'nftViewApiMatic';

const chainNftViewApiDriver: {
  [key in BlockchainNetworkId]: NftViewApiDriverName | undefined;
} = {
  [BlockchainNetworkId.mainnet]: 'nftViewApiEthMainnet',
  [BlockchainNetworkId.ropsten]: undefined,
  [BlockchainNetworkId.rinkeby]: 'nftViewApiEthRinkeby',
  [BlockchainNetworkId.goerli]: undefined,
  [BlockchainNetworkId.dev]: undefined,
  [BlockchainNetworkId.classic]: undefined,
  [BlockchainNetworkId.mordor]: undefined,
  [BlockchainNetworkId.kotti]: undefined,
  [BlockchainNetworkId.smartchain]: 'nftViewApiSmartchain',
  [BlockchainNetworkId.smartchainTestnet]: undefined,
  [BlockchainNetworkId.heco]: 'nftViewApiHeco',
  [BlockchainNetworkId.matic]: 'nftViewApiMatic',
};

function getNftViewApiDriverName(
  chainId: BlockchainNetworkId,
): NftViewApiDriverName {
  return chainNftViewApiDriver[chainId] || 'nftViewApiSmartchain';
}

type NftView2ApiDriverName =
  | 'nftView2ApiEthMainnet'
  | 'nftView2ApiEthRinkeby'
  | 'nftView2ApiSmartchain'
  | 'nftView2ApiHeco'
  | 'nftView2ApiMatic';

const chainNftView2ApiDriver: {
  [key in BlockchainNetworkId]: NftView2ApiDriverName | undefined;
} = {
  [BlockchainNetworkId.mainnet]: 'nftView2ApiEthMainnet',
  [BlockchainNetworkId.ropsten]: undefined,
  [BlockchainNetworkId.rinkeby]: 'nftView2ApiEthRinkeby',
  [BlockchainNetworkId.goerli]: undefined,
  [BlockchainNetworkId.dev]: undefined,
  [BlockchainNetworkId.classic]: undefined,
  [BlockchainNetworkId.mordor]: undefined,
  [BlockchainNetworkId.kotti]: undefined,
  [BlockchainNetworkId.smartchain]: 'nftView2ApiSmartchain',
  [BlockchainNetworkId.smartchainTestnet]: undefined,
  [BlockchainNetworkId.heco]: 'nftView2ApiHeco',
  [BlockchainNetworkId.matic]: 'nftView2ApiMatic',
};

function getNftView2ApiDriverName(
  chainId: BlockchainNetworkId,
): NftView2ApiDriverName {
  return chainNftView2ApiDriver[chainId] || 'nftView2ApiSmartchain';
}

export type DriverName =
  | MainApiDriverName
  | NftViewApiDriverName
  | NftView2ApiDriverName;

export function getTokenByDriver(
  driverName: DriverName,
  unitAddress?: Address,
) {
  if (
    driverName === 'mainApiEthMainnet' ||
    driverName === 'mainApiEthRinkeby'
  ) {
    if (unitAddress === ZERO_ADDRESS) {
      return TokenSymbol.ETH;
    }
  }

  if (driverName === 'mainApiSmartchain') {
    if (unitAddress === ZERO_ADDRESS) {
      return TokenSymbol.BNB;
    }
  }

  if (driverName === 'mainApiHeco') {
    if (unitAddress === ZERO_ADDRESS) {
      return TokenSymbol.HT;
    }
  }

  if (driverName === 'mainApiMatic') {
    if (unitAddress === ZERO_ADDRESS) {
      return TokenSymbol.MATIC;
    }
  }

  return DefaultTokenSymbol;
}

const mainAxios = {
  mainApiEthMainnet: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_ETH_MAINNET,
  }),
  mainApiEthRinkeby: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_RINKEBY,
  }),
  mainApiSmartchain: axios.create({
    baseURL: process.env.REACT_APP_API_BASE,
  }),
  mainApiHeco: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_HECO,
  }),
  mainApiMatic: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_MATIC,
  }),
  mainApiAirdrop: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_AIRDROP,
  }),
};

type mainApiDriversType = keyof typeof mainAxios;

const mainApiDrivers: { [key in mainApiDriversType]: Driver } = {
  ...mainAxios,
};
Object.keys(mainAxios).forEach(key => {
  const initAxios = mainAxios[key as mainApiDriversType];
  initAxios.interceptors.response.use(response => {
    // token invalid
    if ([-1, -2].includes(response.data?.errorCode ?? response.data?.code)) {
      store.dispatch(abortRequests());
      store.dispatch(disconnect());
    }
    return response;
  });
  mainApiDrivers[key as mainApiDriversType] = createAxiosDriver(initAxios);
});

const { requestsReducer, requestsMiddleware } = handleRequests({
  driver: {
    default: createDriver({
      processResponse: response => ({ data: response }),
    }),
    ...mainApiDrivers,
    nftViewApiEthMainnet: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL_ETH_MAINNET,
      }),
    ),
    nftViewApiEthRinkeby: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL_RINKEBY,
      }),
    ),
    nftViewApiSmartchain: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL,
      }),
    ),
    nftViewApiHeco: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL_HECO,
      }),
    ),
    nftViewApiMatic: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL_MATIC,
      }),
    ),
    nftView2ApiEthMainnet: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_NFTVIEW_URL_V2_ETH_MAINNET,
      }),
    ),
    nftView2ApiEthRinkeby: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_NFTVIEW_URL_V2_RINKEBY,
      }),
    ),
    nftView2ApiSmartchain: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_NFTVIEW_URL_V2,
      }),
    ),
    nftView2ApiHeco: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_NFTVIEW_URL_V2_HECO,
      }),
    ),
    nftView2ApiMatic: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_NFTVIEW_URL_V2_MATIC,
      }),
    ),
  },
  onRequest: (request, action, store) => {
    const rootState: RootState = store.getState();

    const { data } = getQuery(rootState, {
      type: setAccount.toString(),
      action: setAccount,
    });

    const chainId =
      data?.chainId ?? getChainId() ?? getNotWeb3WalletInfo()?.chainId;

    console.log('chainId', chainId);

    if (action.meta?.driver === 'axios') {
      action.meta = {
        ...action.meta,
        driver: getMainApiDriverName(chainId),
      };
    } else if (action.meta?.driver === 'airdropAxios') {
      action.meta = {
        ...action.meta,
        driver: getAirdropMainApiDriverName(),
      };
    } else if (action.meta?.driver === 'nftview') {
      action.meta = {
        ...action.meta,
        driver: getNftViewApiDriverName(chainId),
      };
    } else if (action.meta?.driver === 'nftview2') {
      action.meta = {
        ...action.meta,
        driver: getNftView2ApiDriverName(chainId),
      };
    }

    // TODO Throw exception if auth and no token?

    if (action.meta?.auth) {
      return {
        ...request,
        headers: {
          ...request.headers,
          token: data?.token ?? '',
        },
      };
    }
    return request;
  },
  onError: (error, action, store) => {
    if (!action.meta?.suppressErrorNotification) {
      store.dispatch(
        NotificationActions.showNotification({
          message: extractMessage(error),
          severity: 'error',
        }),
      );
    }

    throw error;
  },
});

const sagaMiddleware = createSagaMiddleware();

const rootReducer = combineReducers({
  i18n: persistReducer(i18nPersistConfig, i18nSlice.reducer),
  requests: requestsReducer,
  router: connectRouter(historyInstance),
  notifications: notificationSlice.reducer,
  [LAYOUT_STATE_NAME]: layoutReducer,
  like: likeSlice.reducer,
  user: userSlice.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: [
    ...requestsMiddleware,
    routerMiddleware(historyInstance),
    sagaMiddleware,
  ],
});

export const persistor = persistStore(store);

sagaMiddleware.run(rootSaga);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
